import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Snackbar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  Handshake as HandshakeIcon,
  HourglassEmpty as PendingIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import {
  getCurrentUser,
  discoverPartners,
  createPartnerRequest,
  getAllEvents,
  getPartnerRequests,
} from '../lib/api-client';
import { useMatchNotification } from '../context/MatchNotificationContext';
import type { UserProfile, Event, DivisionLevel, PartnerRequest } from '../types';

const DIVISIONS: DivisionLevel[] = [
  'newcomer',
  'novice',
  'intermediate',
  'advanced',
  'allstar',
  'champion',
  'sophisticated',
  'masters',
];

const divisionLabels: Record<DivisionLevel, string> = {
  newcomer: 'Newcomer',
  novice: 'Novice',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  allstar: 'All-Star',
  champion: 'Champion',
  sophisticated: 'Sophisticated (35+)',
  masters: 'Masters (50+)',
};

export default function PartnerDiscoveryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = getCurrentUser();
  const { triggerMatchSnackbar, showMatchSnackbar, dismissMatchSnackbar } = useMatchNotification();

  const [potentialPartners, setPotentialPartners] = useState<UserProfile[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedEvent, setSelectedEvent] = useState<string>(searchParams.get('eventId') || '');
  const [selectedDivision, setSelectedDivision] = useState<DivisionLevel | ''>('');
  const [selectedRole, setSelectedRole] = useState<'leader' | 'follower' | ''>('');
  const [hidePendingConnections, setHidePendingConnections] = useState(false);

  // Request dialog
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<UserProfile | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);

  // Hide functionality (session-only)
  const [hiddenUserIds, setHiddenUserIds] = useState<Set<string>>(new Set());
  const [lastHiddenUser, setLastHiddenUser] = useState<{
    user: UserProfile;
    index: number;
  } | null>(null);
  const [hideSnackbarOpen, setHideSnackbarOpen] = useState(false);

  // Pending requests tracking
  const [pendingRequestTargetIds, setPendingRequestTargetIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }
    loadEvents();
    loadOutgoingRequests();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (selectedEvent && currentUser) {
      loadPartners();
    }
  }, [selectedEvent, selectedDivision, selectedRole]);

  const loadEvents = async () => {
    try {
      const allEvents = await getAllEvents();
      // Filter to only active/upcoming events
      const upcomingEvents = allEvents.filter((e) => e.isActive || new Date(e.date) > new Date());
      setEvents(upcomingEvents);

      // Auto-select first event user is attending
      if (!selectedEvent && currentUser?.events?.length) {
        const attendingEventId = currentUser.events[0].eventId;
        if (upcomingEvents.find((e) => e.id === attendingEventId)) {
          setSelectedEvent(attendingEventId);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOutgoingRequests = async () => {
    if (!currentUser) return;
    try {
      const outgoing = await getPartnerRequests(currentUser.uid, 'outgoing', 'pending');
      const accepted = await getPartnerRequests(currentUser.uid, 'outgoing', 'accepted');
      const pendingIds = new Set([
        ...outgoing.map((r: PartnerRequest) => r.targetId),
        ...accepted.map((r: PartnerRequest) => r.targetId),
      ]);
      setPendingRequestTargetIds(pendingIds);
    } catch (err: any) {
      console.error('Error loading outgoing requests:', err);
    }
  };

  const loadPartners = async () => {
    if (!currentUser || !selectedEvent) return;

    setLoading(true);
    setError(null);
    try {
      const partners = await discoverPartners({
        userId: currentUser.uid,
        eventId: selectedEvent,
        division: selectedDivision || undefined,
        role: selectedRole || undefined,
      });
      setPotentialPartners(partners);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async () => {
    if (!currentUser || !selectedPartner || !selectedEvent) return;

    const event = events.find((e) => e.id === selectedEvent);
    if (!event) return;

    setSendingRequest(true);
    try {
      // Determine role based on user's division settings
      const role = currentUser.primaryDivision?.role || 'leader';
      const division = currentUser.primaryDivision?.division || 'newcomer';

      const result = await createPartnerRequest({
        requesterId: currentUser.uid,
        requesterName: currentUser.displayName,
        targetId: selectedPartner.uid,
        targetName: selectedPartner.displayName,
        eventId: selectedEvent,
        eventName: event.name,
        competitionId: `${selectedEvent}-${division}`,
        competitionName: `${divisionLabels[division]} Division`,
        division,
        role,
        message: requestMessage,
        expiresAt: event.date,
      });

      // Add to pending requests
      setPendingRequestTargetIds((prev) => new Set([...prev, selectedPartner.uid]));

      setRequestDialogOpen(false);
      setRequestMessage('');

      // Check if it's a mutual match
      if (result.isMutualMatch) {
        triggerMatchSnackbar();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSendingRequest(false);
    }
  };

  const handleHideUser = (user: UserProfile, index: number) => {
    setLastHiddenUser({ user, index });
    setHiddenUserIds((prev) => new Set([...prev, user.uid]));
    setHideSnackbarOpen(true);
  };

  const handleUndoHide = () => {
    if (lastHiddenUser) {
      setHiddenUserIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(lastHiddenUser.user.uid);
        return newSet;
      });
      setLastHiddenUser(null);
    }
    setHideSnackbarOpen(false);
  };

  const handleDismissHideSnackbar = () => {
    setHideSnackbarOpen(false);
    setLastHiddenUser(null);
  };

  const openRequestDialog = (partner: UserProfile) => {
    setSelectedPartner(partner);
    setRequestDialogOpen(true);
  };

  const handleViewMatch = () => {
    dismissMatchSnackbar();
    navigate('/profile?view=requests');
  };

  // Filter partners based on hidden and pending filters
  const visiblePartners = potentialPartners.filter((partner) => {
    if (hiddenUserIds.has(partner.uid)) return false;
    if (hidePendingConnections && pendingRequestTargetIds.has(partner.uid)) return false;
    return true;
  });

  if (!currentUser) {
    return null;
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Find a Partner
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Event</InputLabel>
              <Select
                value={selectedEvent}
                label="Event"
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                {events.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.name} - {new Date(event.date).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Division</InputLabel>
                <Select
                  value={selectedDivision}
                  label="Division"
                  onChange={(e) => setSelectedDivision(e.target.value as DivisionLevel | '')}
                >
                  <MenuItem value="">All Divisions</MenuItem>
                  {DIVISIONS.map((div) => (
                    <MenuItem key={div} value={div}>
                      {divisionLabels[div]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Looking for</InputLabel>
                <Select
                  value={selectedRole}
                  label="Looking for"
                  onChange={(e) => setSelectedRole(e.target.value as 'leader' | 'follower' | '')}
                >
                  <MenuItem value="">Any Role</MenuItem>
                  <MenuItem value="leader">Leader</MenuItem>
                  <MenuItem value="follower">Follower</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={hidePendingConnections}
                  onChange={(e) => setHidePendingConnections(e.target.checked)}
                  size="small"
                />
              }
              label="Hide pending connections"
              sx={{ ml: 0 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : !selectedEvent ? (
        <Alert severity="info">Select an event to find potential partners.</Alert>
      ) : visiblePartners.length === 0 ? (
        <Alert severity="info">
          No potential partners found for this event with your current filters. Try adjusting your
          filters or check back later.
        </Alert>
      ) : (
        <>
          {/* Partner count */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {visiblePartners.length} potential partner{visiblePartners.length !== 1 ? 's' : ''}
          </Typography>

          {/* Partner List */}
          <List sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
            {visiblePartners.map((partner, index) => {
              const isPending = pendingRequestTargetIds.has(partner.uid);

              return (
                <ListItem
                  key={partner.uid}
                  divider={index < visiblePartners.length - 1}
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      {isPending ? (
                        <IconButton
                          disabled
                          sx={{
                            color: 'warning.main',
                            '&.Mui-disabled': {
                              color: 'warning.main',
                            },
                          }}
                          title="Pending Connection"
                        >
                          <PendingIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => openRequestDialog(partner)}
                          sx={{
                            color: 'success.main',
                            '&:hover': {
                              bgcolor: 'success.light',
                              color: 'white',
                            },
                          }}
                          title="Send Connection Request"
                        >
                          <HandshakeIcon />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleHideUser(partner, index)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'white',
                          },
                        }}
                        title="Hide"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      src={partner.profilePicture}
                      sx={{
                        width: 48,
                        height: 48,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate(`/users/${partner.uid}`)}
                    >
                      {partner.displayName?.[0] || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${partner.uid}`)}
                      >
                        {partner.displayName}
                      </Typography>
                    }
                    secondary={
                      partner.location && (
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <LocationIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary', fontSize: 16 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {partner.location}
                          </Typography>
                        </Box>
                      )
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {/* Request Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Partner Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Send a partner request to {selectedPartner?.displayName} for{' '}
            {events.find((e) => e.id === selectedEvent)?.name}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Personal message (optional)"
            placeholder="Hi! I'd love to partner with you for this event..."
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSendRequest} variant="contained" disabled={sendingRequest}>
            {sendingRequest ? <CircularProgress size={20} /> : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hide User Snackbar - persists until dismissed */}
      <Snackbar
        open={hideSnackbarOpen}
        message={`${lastHiddenUser?.user.displayName || 'User'} hidden`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <Stack direction="row" spacing={1}>
            <Button color="primary" size="small" onClick={handleUndoHide}>
              Undo
            </Button>
            <Button color="inherit" size="small" onClick={handleDismissHideSnackbar}>
              Dismiss
            </Button>
          </Stack>
        }
      />

      {/* Match Snackbar - auto-dismiss after 6 seconds */}
      <Snackbar
        open={showMatchSnackbar}
        autoHideDuration={6000}
        onClose={dismissMatchSnackbar}
        message="You've found a match!"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <Stack direction="row" spacing={1}>
            <Button color="primary" size="small" onClick={handleViewMatch}>
              View
            </Button>
            <Button color="inherit" size="small" onClick={dismissMatchSnackbar}>
              Dismiss
            </Button>
          </Stack>
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            bgcolor: 'success.main',
          },
        }}
      />
    </Container>
  );
}
